import React, { useEffect, useState } from 'react';
import './TopLevelStocks.css';
import { FaArrowLeft, FaSync } from 'react-icons/fa';
import { API_BASE_URL } from '../config';
import WatchlistStar from './WatchlistStar';

const TopGainers = ({ onBack }) => {
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('gainers');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE_URL}/top-low-level`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'fo' }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Process top performers (gainers)
      const gainersData = Array.isArray(data.top_performers)
        ? data.top_performers.map((item, idx) => ({
            id: item.rank_near_high || idx + 1,
            name: item.stock || '—',
            change: item.percentage_change ? `${Number(item.percentage_change).toFixed(2)}%` : '—',
            highlight: item.rank_near_high === 1
          }))
        : [];

      // Process low performers (losers)
      const losersData = Array.isArray(data.low_performers)
        ? data.low_performers.map((item, idx) => ({
            id: item.rank_near_low || idx + 1,
            name: item.stock || '—',
            change: item.percentage_change ? `${Number(item.percentage_change).toFixed(2)}%` : '—',
            highlight: item.rank_near_low === 1
          }))
        : [];

      setGainers(gainersData);
      setLosers(losersData);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const renderTable = (data, isGainers) => (
    <div className="vb-table">
      <div className="vb-table-header">
        <span>#</span>
        <span>Name</span>
        <span>Change</span>
      </div>
      {data.map((row) => (
        <div key={row.id} className={`vb-table-row ${row.highlight ? 'highlight' : ''}`}>
          <span>{row.highlight ? '⭐' : row.id}</span>
          <span>
            <WatchlistStar 
              stock={{
                symbol: row.name,
                name: row.name
              }}
            />
            {row.name}
          </span>
          <span>
            <span className={`change-badge ${isGainers ? 'positive' : 'negative'}`}>
              {row.change}
            </span>
          </span>
        </div>
      ))}
    </div>
  );

  const renderSkeletonTable = () => (
    <div className="vb-table skeleton-table">
      <div className="vb-table-header">
        <span>#</span>
        <span>Name</span>
        <span>Change</span>
      </div>
      <div className="loading-container">
        <div className="loading-circle"></div>
      </div>
    </div>
  );

  const renderState = () => {
    if (loading) return renderSkeletonTable();
    if (error) return <div className="error-block">{error}</div>;

    const isGainers = activeTab === 'gainers';
    const data = isGainers ? gainers : losers;

    if (!data.length) {
      return (
        <div className="empty-state">
          <p>No data available right now.</p>
        </div>
      );
    }

    return renderTable(data, isGainers);
  };

  return (
    <div className="top-page">
      <div className="top-header">
        <button className="back-button" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <h1>Market Movers</h1>
        <button 
          className={`refresh-button ${refreshing ? 'spinning' : ''}`} 
          onClick={handleRefresh}
          disabled={loading || refreshing}
        >
          <FaSync />
        </button>
      </div>

      <div className="vb-content">
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'gainers' ? 'active' : ''}`}
            onClick={() => setActiveTab('gainers')}
          >
            Top Gainers 📈
          </button>
          <button 
            className={`tab-button ${activeTab === 'losers' ? 'active' : ''}`}
            onClick={() => setActiveTab('losers')}
          >
            Top Losers 📉
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'gainers' ? (
            <div className="vb-category-card">
              <h3>Top Gainers</h3>
              <p>Stocks with highest price increase today</p>
            </div>
          ) : (
            <div className="vb-category-card">
              <h3>Top Losers</h3>
              <p>Stocks with highest price decrease today</p>
            </div>
          )}

          {renderState()}
        </div>
      </div>
    </div>
  );
};

export default TopGainers;
